import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Card,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Space,
  Tag,
  Modal,
  message,
  Typography,
  Row,
  Col,
  Statistic,
  Tooltip,
  Popconfirm,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  DeleteOutlined,
  EyeOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import {
  getApiLogs,
  getApiLogDetail,
  getApiLogStatistics,
  cleanupApiLogs,
  ApiLog,
  ApiLogQueryParams,
  ApiLogStatistics,
} from '../api/api-log';
import { TimeUtil } from '../utils/timeUtil';
import { handleApiError } from '../utils/errorHandler';
import { useAuthStore } from '../store/authStore';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text, Title } = Typography;

const ApiLogList: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<ApiLog[]>([]);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
  });
  const [filters, setFilters] = useState<ApiLogQueryParams>({});
  const [statistics, setStatistics] = useState<ApiLogStatistics | null>(null);
  const currentPage = pagination.current;
  const pageSize = pagination.pageSize;
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedLog, setSelectedLog] = useState<ApiLog | null>(null);
  const [openingDetailId, setOpeningDetailId] = useState<number | null>(null);
  const [form] = Form.useForm();
  // 仅 admin 可查看/筛选所有帐号的日志；其他角色后端强制只看自己，前端相应隐藏「用户」筛选、列与清理入口
  const authStore = useAuthStore();
  const isAdmin = authStore.role === 'admin';

  // 获取日志列表
  const fetchLogs = useCallback(async (params: ApiLogQueryParams = {}) => {
    try {
      setLoading(true);
      const response = await getApiLogs({
        ...params,
        page: currentPage,
        limit: pageSize,
      });
      
      setLogs(response.data.data);
      setTotal(response.data.total);
    } catch (error) {
      handleApiError(error, '获取API日志失败');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize]);

  // 获取统计信息
  const fetchStatistics = useCallback(async () => {
    try {
      const response = await getApiLogStatistics();
      setStatistics(response.data);
    } catch (error) {
      handleApiError(error, '获取统计信息失败', false); // 不显示错误消息
    }
  }, []);

  useEffect(() => {
    fetchLogs(filters);
    fetchStatistics();
  }, [fetchLogs, fetchStatistics, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // 搜索处理
  const handleSearch = (values: any) => {
    const newFilters: ApiLogQueryParams = {
      ...values,
      startDate: values.dateRange?.[0]?.format('YYYY-MM-DD'),
      endDate: values.dateRange?.[1]?.format('YYYY-MM-DD'),
    };
    delete newFilters.dateRange;
    
    setFilters(newFilters);
    setPagination({ ...pagination, current: 1 });
    fetchLogs(newFilters);
  };

  // 重置搜索
  const handleReset = () => {
    form.resetFields();
    setFilters({});
    setPagination({ ...pagination, current: 1 });
    fetchLogs({});
  };

  // 列表接口不返回 request/response 大字段，详情单独按 id 拉取
  const handleViewDetail = async (record: ApiLog) => {
    setOpeningDetailId(record.id);
    try {
      const response = await getApiLogDetail(record.id);
      setSelectedLog(response.data);
      setDetailModalVisible(true);
    } catch (error) {
      handleApiError(error, '获取日志详情失败');
    } finally {
      setOpeningDetailId(null);
    }
  };

  // 清理旧日志
  const handleCleanup = async (days: number = 30) => {
    try {
      const response = await cleanupApiLogs(days);
      message.success(response.data.message || `成功清理 ${response.data.deletedCount} 条旧日志`);
      fetchLogs(filters);
      fetchStatistics();
    } catch (error) {
      handleApiError(error, '清理日志失败');
    }
  };

  // 状态码颜色映射
  const getStatusColor = (status: number) => {
    if (status >= 200 && status < 300) return 'success';
    if (status >= 300 && status < 400) return 'warning';
    if (status >= 400 && status < 500) return 'error';
    if (status >= 500) return 'error';
    return 'default';
  };

  // 方法颜色映射
  const getMethodColor = (method: string) => {
    const colors: { [key: string]: string } = {
      GET: 'blue',
      POST: 'green',
      PUT: 'orange',
      DELETE: 'red',
      PATCH: 'purple',
    };
    return colors[method] || 'default';
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户',
      dataIndex: 'user',
      key: 'user',
      width: 100,
      render: (text: string | null) => text || '-',
    },
    {
      title: '方法',
      dataIndex: 'method',
      key: 'method',
      width: 80,
      render: (method: string) => (
        <Tag color={getMethodColor(method)}>{method}</Tag>
      ),
    },
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <Text code>{text}</Text>
        </Tooltip>
      ),
    },
    {
      title: '状态码',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: number) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      ),
    },
    {
      title: 'IP地址',
      dataIndex: 'ip',
      key: 'ip',
      width: 120,
      render: (text: string | null) => text || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 180,
      render: (text: string) => TimeUtil.formatServerTime(text),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_: any, record: ApiLog) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            loading={openingDetailId === record.id}
            onClick={() => handleViewDetail(record)}
          >
            详情
          </Button>
        </Space>
      ),
    },
  ].filter((col) => isAdmin || col.key !== 'user'); // 非 admin 隐藏「用户」列

  return (
    <div>
      <Title level={2}>API日志管理</Title>
      
      {/* 统计卡片 */}
      {statistics && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="总请求数"
                value={statistics.totalRequests}
                prefix={<BarChartOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="成功请求"
                value={statistics.statusStats.find(s => s.status >= 200 && s.status < 300)?.count || 0}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="客户端错误"
                value={statistics.statusStats.find(s => s.status >= 400 && s.status < 500)?.count || 0}
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="服务器错误"
                value={statistics.statusStats.find(s => s.status >= 500)?.count || 0}
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* 搜索表单 */}
      <Card style={{ marginBottom: 16 }}>
        <Form
          form={form}
          layout="inline"
          onFinish={handleSearch}
          style={{ marginBottom: 16 }}
        >
          <Form.Item name="user" label="用户" hidden={!isAdmin}>
            <Input placeholder="请输入用户名" />
          </Form.Item>
          <Form.Item name="method" label="方法">
            <Select placeholder="请选择方法" allowClear>
              <Option value="GET">GET</Option>
              <Option value="POST">POST</Option>
              <Option value="PUT">PUT</Option>
              <Option value="DELETE">DELETE</Option>
              <Option value="PATCH">PATCH</Option>
            </Select>
          </Form.Item>
          <Form.Item name="url" label="URL">
            <Input placeholder="请输入URL关键词" />
          </Form.Item>
          <Form.Item name="status" label="状态码">
            <Select placeholder="请选择状态码" allowClear>
              <Option value={200}>200 - 成功</Option>
              <Option value={400}>400 - 客户端错误</Option>
              <Option value={401}>401 - 未授权</Option>
              <Option value={403}>403 - 禁止访问</Option>
              <Option value={404}>404 - 未找到</Option>
              <Option value={500}>500 - 服务器错误</Option>
            </Select>
          </Form.Item>
          <Form.Item name="ip" label="IP地址">
            <Input placeholder="请输入IP地址" />
          </Form.Item>
          <Form.Item name="dateRange" label="时间范围">
            <RangePicker />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                搜索
              </Button>
              <Button onClick={handleReset} icon={<ReloadOutlined />}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
        
        <Space>
          {isAdmin && (
            <Popconfirm
              title="确定要清理30天前的日志吗？"
              onConfirm={() => handleCleanup(30)}
              okText="确定"
              cancelText="取消"
            >
              <Button danger icon={<DeleteOutlined />}>
                清理旧日志
              </Button>
            </Popconfirm>
          )}
        </Space>
      </Card>

      {/* 日志表格 */}
      <Card>
        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
            onChange: (page, pageSize) => {
              setPagination({ current: page, pageSize: pageSize || 20 });
            },
          }}
        />
      </Card>

      {/* 详情模态框 */}
      <Modal
        title="API日志详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedLog && (
          <div>
            <Row gutter={16}>
              <Col span={12}>
                <Text strong>ID: </Text>
                <Text>{selectedLog.id}</Text>
              </Col>
              <Col span={12}>
                <Text strong>用户: </Text>
                <Text>{selectedLog.user || '-'}</Text>
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={12}>
                <Text strong>方法: </Text>
                <Tag color={getMethodColor(selectedLog.method)}>{selectedLog.method}</Tag>
              </Col>
              <Col span={12}>
                <Text strong>状态码: </Text>
                <Tag color={getStatusColor(selectedLog.status)}>{selectedLog.status}</Tag>
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={24}>
                <Text strong>URL: </Text>
                <Text code>{selectedLog.url}</Text>
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={12}>
                <Text strong>IP地址: </Text>
                <Text>{selectedLog.ip || '-'}</Text>
              </Col>
              <Col span={12}>
                <Text strong>创建时间: </Text>
                <Text>{TimeUtil.formatServerTime(selectedLog.createTime)}</Text>
              </Col>
            </Row>
            
            {selectedLog.request && (
              <div style={{ marginTop: 16 }}>
                <Text strong>请求数据:</Text>
                <pre style={{ 
                  background: '#f5f5f5', 
                  padding: 12, 
                  borderRadius: 4,
                  maxHeight: 200,
                  overflow: 'auto'
                }}>
                  {JSON.stringify(JSON.parse(selectedLog.request), null, 2)}
                </pre>
              </div>
            )}
            
            {selectedLog.response && (
              <div style={{ marginTop: 16 }}>
                <Text strong>响应数据:</Text>
                <pre style={{ 
                  background: '#f5f5f5', 
                  padding: 12, 
                  borderRadius: 4,
                  maxHeight: 200,
                  overflow: 'auto'
                }}>
                  {JSON.stringify(JSON.parse(selectedLog.response), null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ApiLogList;
